import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ImagePicker from './ImagePicker';

const AdminSponsorForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    logo: '',
    description: '',
    why_partnered: '',
    website: '',
    testimonial: {
      name: '',
      role: '',
      content: ''
    },
    is_active: true
  });

  const [showImagePicker, setShowImagePicker] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (isEditMode) {
      fetchSponsor();
    }
  }, [id]);

  const fetchSponsor = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch sponsor details');

      const sponsor = await response.json();
      setFormData({
        name: sponsor.name || '',
        category: sponsor.category || '',
        logo: sponsor.logo || '',
        description: sponsor.description || '',
        why_partnered: sponsor.why_partnered || sponsor.whyPartnered || '',
        website: sponsor.website || '',
        testimonial: {
          name: sponsor.testimonial?.name || '',
          role: sponsor.testimonial?.role || '',
          content: sponsor.testimonial?.content || ''
        },
        is_active: sponsor.is_active !== undefined ? sponsor.is_active : (sponsor.isActive !== undefined ? sponsor.isActive : true)
      });
    } catch (error) {
      console.error('Error fetching sponsor:', error);
      showAlert('Error', 'Failed to load sponsor details. Please try again.', () => navigate('/admin/sponsors'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        showAlert('Authentication Required', 'Please log in again.', () => navigate('/admin/login'));
        return;
      }

      const url = isEditMode 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/sponsors/${id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/sponsors`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} sponsor`);

      showAlert('Success', `Sponsor has been successfully ${isEditMode ? 'updated' : 'created'}!`, () => {
        navigate('/admin/sponsors');
      });
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} sponsor:`, error);
      showAlert('Error', `Failed to ${isEditMode ? 'update' : 'create'} sponsor: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-electric-yellow text-xl font-bold animate-pulse">Loading sponsor details...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow">
          {isEditMode ? 'Edit Sponsor' : 'Create Sponsor'}
        </h2>
        <button
          onClick={() => navigate('/admin/sponsors')}
          className="w-full sm:w-auto bg-charcoal/40 text-warm-white px-6 py-3 rounded-xl font-medium hover:bg-charcoal/60 transition-colors duration-300 text-center"
        >
          ← Back to Sponsors
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* Basic Info */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="e.g., FlowState"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Category *
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="e.g., Productivity, Data & Insights"
              />
            </div>
          </div>
        </div>

        {/* Media */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Media</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-soft-gray mb-2">
              Logo Image URL *
            </label>
            
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="w-full sm:w-48 h-48 rounded-xl border-2 border-dashed border-slate-dark/50 bg-midnight/40 flex flex-col items-center justify-center overflow-hidden flex-shrink-0 relative group">
                {formData.logo ? (
                  <>
                    <img src={formData.logo} alt="Logo preview" className="w-full h-full object-contain p-2" />
                    <div className="absolute inset-0 bg-midnight/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button 
                        type="button"
                        onClick={() => setShowImagePicker(true)}
                        className="bg-electric-yellow text-midnight px-4 py-2 rounded-lg font-bold text-sm transform transition-all duration-300 scale-95 group-hover:scale-100 shadow-lg shadow-electric-yellow/20"
                      >
                        Change Photo
                      </button>
                    </div>
                  </>
                ) : (
                  <button 
                    type="button"
                    onClick={() => setShowImagePicker(true)}
                    className="flex flex-col items-center justify-center w-full h-full gap-2 text-soft-gray hover:text-electric-yellow hover:bg-white/5 transition-all duration-300"
                  >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">Select Image</span>
                  </button>
                )}
              </div>
              <div className="flex-1 w-full">
                <input
                  type="url"
                  name="logo"
                  value={formData.logo}
                  onChange={handleChange}
                  required
                  className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white text-sm placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50 mt-2"
                  placeholder="Selected image URL will appear here..."
                  readOnly
                />
                <p className="text-soft-gray text-sm mt-3">
                  Click the placeholder box to select an image from your Cloudflare Media Library.
                </p>
              </div>
            </div>
            
            {showImagePicker && (
              <ImagePicker 
                onSelect={(url) => {
                  setFormData(prev => ({ ...prev, logo: url }));
                  setShowImagePicker(false);
                }}
                onClose={() => setShowImagePicker(false)}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Content</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows={3}
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-electric-yellow placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="Company description..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Why We Partnered *
              </label>
              <textarea
                name="why_partnered"
                value={formData.why_partnered}
                onChange={handleChange}
                required
                rows={3}
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="Explanation of partnership..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Website URL *
              </label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                required
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="Website URL (for Visit [Name] button)"
              />
            </div>
          </div>
        </div>

        {/* Testimonial Section */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Testimonial</h3>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-soft-gray mb-2">
                Testimonial Quote (optional)
              </label>
              <textarea
                name="testimonial.content"
                value={formData.testimonial.content}
                onChange={handleChange}
                rows={3}
                className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                placeholder="Testimonial Quote"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-soft-gray mb-2">
                  Testimonial Author Name (optional)
                </label>
                <input
                  type="text"
                  name="testimonial.name"
                  value={formData.testimonial.name}
                  onChange={handleChange}
                  className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                  placeholder="e.g., Jane Doe"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-soft-gray mb-2">
                  Testimonial Author Title (optional)
                </label>
                <input
                  type="text"
                  name="testimonial.role"
                  value={formData.testimonial.role}
                  onChange={handleChange}
                  className="w-full bg-midnight/80 border border-slate-dark rounded-xl px-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
                  placeholder="e.g., CEO"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
          <h3 className="font-bold text-electric-yellow text-xl mb-6">Status</h3>
          
          <label className="flex items-start gap-3 p-4 bg-midnight/40 rounded-xl border border-slate-dark/30">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              className="w-5 h-5 mt-1 rounded border-slate-dark bg-midnight/80 text-electric-yellow focus:ring-electric-yellow/50"
            />
            <div className="text-left">
              <span className="text-warm-white font-medium text-left">Is Active</span>
              <p className="text-soft-gray text-sm text-left">Check to show this sponsor on the website</p>
            </div>
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="w-full sm:w-auto bg-electric-yellow text-midnight px-8 py-4 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 disabled:opacity-50"
          >
            {saving ? 'Saving...' : isEditMode ? 'Update Sponsor' : 'Create Sponsor'}
          </button>
        </div>
      </form>

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-center gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSponsorForm;
